import React from "react";
import { useNavigate } from "react-router-dom";
import {
  useMyInProgressChallenges,
  useMyCompletedChallenges,
} from "../campaigns/hooks/use-my-challenges";
import { formatCampaignTime } from "../campaigns/utils/campaign-utils";
import { SkeletonWave } from "../../components/common/skeleton-wave";
import Header from "../../components/common/header";

const MyChallengesPage = () => {
  const navigate = useNavigate();
  const {
    challenges: inProgressChallenges,
    loading: inProgressLoading,
  } = useMyInProgressChallenges();
  const {
    challenges: completedChallenges,
    loading: completedLoading,
  } = useMyCompletedChallenges();

  const loading = inProgressLoading || completedLoading;
  const hasAnyChallenges =
    inProgressChallenges.length > 0 || completedChallenges.length > 0;

  const getChannelName = (channel: string) => {
    switch (channel) {
      case "NAVER":
        return "네이버";
      case "COUPANG":
        return "쿠팡";
      case "CARROT":
        return "당근";
      default:
        return channel;
    }
  };

  const getStatusText = (challenge: any) => {
    const status = challenge.status;
    const startAt = new Date(challenge.run?.startAt);
    const now = new Date();
    
    // 챌린지 시작 전인 경우 디데이 계산
    if (startAt > now && (status === "JOINED" || status === "IN_PROGRESS")) {
      const diffTime = startAt.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return `D-${diffDays}`;
    }
    
    switch (status) {
      case "JOINED":
        return "인증 진행 중";
      case "IN_PROGRESS":
        return "인증 진행 중";
      case "COMPLETED":
        return "성공";
      case "FAILED":
        return "실패";
      default:
        return status;
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "JOINED":
      case "IN_PROGRESS":
        return "bg-blue-100 text-blue-700";
      case "COMPLETED":
        return "bg-green-100 text-green-700";
      case "FAILED":
        return "bg-gray-100 text-gray-600";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <Header title="내 챌린지" showBackButton={true} />
        <SkeletonWave className="pt-8" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="내 챌린지" showBackButton={true} />
      <div className="max-w-4xl mx-auto p-4">
        {!hasAnyChallenges ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-2">아직 참여한 챌린지가 없어요</p>
            <p className="text-gray-500">
              미션 할인 챌린지에 참여해보세요!
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* 진행중인 챌린지 섹션 */}
            {inProgressChallenges.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-black mb-4">
                  진행중인 챌린지
                </h2>
                <div className="space-y-4">
                  {inProgressChallenges.map((challenge: any) => {
                    const campaign = challenge.run?.campaign;
                    if (!campaign) return null;

                    return (
                      <div
                        key={challenge.id}
                        onClick={() =>
                          navigate(
                            `/campaigns/participation/${challenge.id}`
                          )
                        }
                        className="bg-white rounded-lg shadow-md p-4 cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-blue-500"
                      >
                        <div className="flex gap-4">
                          {campaign.productImageUrl && (
                            <img
                              src={campaign.productImageUrl}
                              alt={campaign.productName}
                              className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                            />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded">
                                {getChannelName(campaign.channel)}
                              </span>
                              <span
                                className={`text-xs font-bold px-2 py-1 rounded ${getStatusBadgeColor(
                                  challenge.status
                                )}`}
                              >
                                {getStatusText(challenge)}
                              </span>
                            </div>
                            <h3 className="font-semibold text-lg mb-2">
                              {campaign.productName}
                            </h3>
                            <div className="flex flex-col gap-2 text-sm">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold">
                                  {campaign.discountPrice?.toLocaleString()}원
                                </span>
                                {campaign.originalPrice && (
                                  <span className="text-sm text-gray-400 line-through">
                                    {campaign.originalPrice.toLocaleString()}원
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-100 text-blue-700 font-bold text-xs">
                                  페이백{" "}
                                  {campaign.paybackPrice?.toLocaleString()}원
                                </span>
                              </div>
                            </div>
                            <div className="mt-2 text-sm text-gray-600">
                              기간: {formatCampaignTime(challenge.run.startAt)} ~{" "}
                              {formatCampaignTime(challenge.run.endAt)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 완료된 챌린지 섹션 */}
            {completedChallenges.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-black mb-4">
                  완료된 챌린지
                </h2>
                <div className="space-y-4">
                  {completedChallenges.map((challenge: any) => {
                    const campaign = challenge.run?.campaign;
                    if (!campaign) return null;

                    const isCompleted = challenge.status === "COMPLETED";
                    const isFailed = challenge.status === "FAILED";

                    return (
                      <div
                        key={challenge.id}
                        onClick={() =>
                          navigate(
                            `/campaigns/participation/${challenge.id}`
                          )
                        }
                        className="bg-white rounded-lg shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow opacity-90"
                      >
                        <div className="flex gap-4">
                          {campaign.productImageUrl && (
                            <img
                              src={campaign.productImageUrl}
                              alt={campaign.productName}
                              className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                            />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded">
                                {getChannelName(campaign.channel)}
                              </span>
                              <span
                                className={`text-xs font-bold px-2 py-1 rounded ${getStatusBadgeColor(
                                  challenge.status
                                )}`}
                              >
                                {getStatusText(challenge)}
                              </span>
                            </div>
                            <h3 className="font-semibold text-lg mb-2">
                              {campaign.productName}
                            </h3>
                            <div className="flex flex-col gap-2 text-sm">
                              {isCompleted && (
                                <div className="flex items-center gap-2">
                                  <span className="inline-flex items-center px-2 py-1 rounded-md bg-green-100 text-green-700 font-bold text-xs">
                                    페이백{" "}
                                    {campaign.paybackPrice?.toLocaleString()}원
                                  </span>
                                </div>
                              )}
                              {isFailed && (
                                <p className="text-sm text-gray-500">
                                  이번 챌린지는 아쉽게 완료되지 않았어요
                                </p>
                              )}
                            </div>
                            <div className="mt-2 text-sm text-gray-600">
                              기간: {formatCampaignTime(challenge.run.startAt)} ~{" "}
                              {formatCampaignTime(challenge.run.endAt)}
                            </div>
                            {challenge.completedAt && (
                              <div className="mt-1 text-xs text-gray-500">
                                완료일:{" "}
                                {formatCampaignTime(challenge.completedAt)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 진행중 챌린지가 없을 때 */}
            {inProgressChallenges.length === 0 &&
              completedChallenges.length > 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500">
                    지금 참여 중인 챌린지가 없어요
                  </p>
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyChallengesPage;
